import { addDays, format, parseISO } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import {
  CarrierType,
  InboundStatus,
  InboundStatuses,
  OutboundStatus,
  OutboundStatusLabels,
} from "../../../utils/constants";
import {
  getIANATimezone,
  getYardCalendarDaysInclusive,
} from "../../../utils/dateUtils";

export const RailcarDirection = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND",
};

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
};

// One ISO form for every date on a row, so the grid's client-side sort
// compares like with like whatever shape the record stored.
const toIso = (value) => {
  const time = toTime(value);
  return time === null ? null : new Date(time).toISOString();
};

const pickByTime = (values, pick) => {
  const times = values.map(toTime).filter((time) => time !== null);
  return times.length ? new Date(pick(...times)).toISOString() : null;
};

/**
 * The period as instants: from the yard's midnight starting the first day up
 * to, not including, the yard's midnight after the last day. The days are the
 * yard's, so the same report run from another time zone counts the same cars.
 */
export const getPeriodBounds = (startDate, endDate) => {
  const timeZone = getIANATimezone();
  const toInstant = (day) =>
    timeZone ? zonedTimeToUtc(`${day}T00:00:00`, timeZone) : parseISO(day);
  const dayAfterEnd = format(addDays(parseISO(endDate), 1), "yyyy-MM-dd");
  return {
    startIso: toInstant(startDate).toISOString(),
    endIso: toInstant(dayAfterEnd).toISOString(),
  };
};

// The events that put a car on site and take it off, per direction. Inbound
// arrival is the notify time: that is the date the ARRIVED status keys off.
// A car leaves at the later of its release and its pull, since many yards
// never record a pull.
const presenceFields = {
  [RailcarDirection.INBOUND]: {
    arrived: ["notifyDateTime", "placedDateTime"],
    left: ["releaseDateTime", "collectedDateTime"],
    releasedStatus: InboundStatus.RELEASED,
  },
  [RailcarDirection.OUTBOUND]: {
    arrived: ["arrivalDateTime", "placementDateTime"],
    left: ["departureDateTime", "collectedDateTime"],
    releasedStatus: OutboundStatus.RELEASED,
  },
};

/**
 * Search filter for the rail shipments that could have been on site during
 * the period: arrived before it ended and not gone before it started. It
 * over-selects (a car pulled before the period but never released still
 * matches), so isOnSiteDuring decides each row.
 */
export const buildRailcarFilter = (
  direction,
  { startIso, endIso, reloadYardId, accountId },
) => {
  const { arrived, left, releasedStatus } = presenceFields[direction];
  const filter = {
    reloadYardId: { eq: reloadYardId },
    carrierType: { eq: CarrierType.RAIL },
    and: [
      // A range never matches a missing date, so cars still in transit drop
      // out here.
      { or: arrived.map((field) => ({ [field]: { lt: endIso } })) },
      {
        or: [
          { status: { ne: releasedStatus } },
          ...left.map((field) => ({ [field]: { gte: startIso } })),
        ],
      },
    ],
  };
  if (accountId) filter.accountId = { eq: accountId };
  return filter;
};

// The date columns show what each record stored. The presence window
// (onSiteFrom / onSiteUntil) is derived from them: a car placed with no
// notify time still arrived when it was placed.
const finishRow = (row, now) => {
  const { arrived, left } = presenceFields[row.direction];
  const onSiteFrom = pickByTime(
    arrived.map((field) => row.source[field]),
    Math.min,
  );
  const onSiteUntil = pickByTime(
    left.map((field) => row.source[field]),
    Math.max,
  );
  const { source, ...rest } = row;
  return {
    ...rest,
    onSiteFrom,
    onSiteUntil,
    onSite: !onSiteUntil,
    daysOnSite: getYardCalendarDaysInclusive(onSiteFrom, onSiteUntil || now),
  };
};

export const toInboundRailcarRow = (shipment, now = new Date()) =>
  finishRow(
    {
      source: shipment,
      id: shipment.id,
      direction: RailcarDirection.INBOUND,
      sit: Boolean(shipment.sit),
      railCarNumber: shipment.railCarNumber,
      accountName: shipment.accountName,
      carrierName: shipment.carrierName,
      status: shipment.status,
      statusLabel:
        InboundStatuses.find((itm) => itm.id === shipment.status)?.name ||
        shipment.status,
      arrivedAt: toIso(shipment.notifyDateTime),
      placedAt: toIso(shipment.placedDateTime),
      releasedAt: toIso(shipment.releaseDateTime),
      pulledAt: toIso(shipment.collectedDateTime),
    },
    now,
  );

export const toOutboundRailcarRow = (shipment, now = new Date()) =>
  finishRow(
    {
      source: shipment,
      id: shipment.id,
      direction: RailcarDirection.OUTBOUND,
      sit: false,
      railCarNumber: shipment.railCarNumber,
      accountName: shipment.accountName,
      // Legacy rows the admin carrier backfill has not reached yet still
      // carry the name on the old `carrier` scalar.
      carrierName: shipment.carrierName || shipment.carrier,
      status: shipment.status,
      statusLabel: OutboundStatusLabels[shipment.status] || shipment.status,
      arrivedAt: toIso(shipment.arrivalDateTime),
      // Records placed before the placement backfill ran hold the placed time
      // in arrivalDateTime.
      placedAt: toIso(shipment.placementDateTime || shipment.arrivalDateTime),
      releasedAt: toIso(shipment.departureDateTime),
      pulledAt: toIso(shipment.collectedDateTime),
    },
    now,
  );

/** On site at any moment of [startIso, endIso): arrived before the end, not gone before the start. */
export const isOnSiteDuring = (row, startIso, endIso) => {
  const from = toTime(row.onSiteFrom);
  if (from === null || from >= toTime(endIso)) return false;
  const until = toTime(row.onSiteUntil);
  return until === null || until >= toTime(startIso);
};

export const summarizeRailcars = (rows) =>
  rows.reduce(
    (totals, row) => {
      totals.total += 1;
      if (row.direction === RailcarDirection.INBOUND) totals.inbound += 1;
      else totals.outbound += 1;
      if (row.sit) totals.sit += 1;
      if (row.onSite) totals.onSite += 1;
      return totals;
    },
    { total: 0, inbound: 0, sit: 0, outbound: 0, onSite: 0 },
  );
